import { useEffect, useState } from "react";
import { Shield, Flag, Eye, EyeOff, UserX, AlertTriangle } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { Badge } from "@/components/Badge";
import { Avatar } from "@/components/AppShell";
import { Button } from "@/components/Button";
import { useToast } from "@/components/Toast";
import { cn } from "@/lib/cn";
import type { Strain, User } from "@/types";

export default function Admin() {
  const toast = useToast((s) => s.push);
  const [tab, setTab] = useState<"reports" | "users" | "strains">("reports");
  const [reports, setReports] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [strains, setStrains] = useState<Strain[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiClient.adminReports(),
      apiClient.adminUsers(),
      apiClient.adminStrains(),
    ])
      .then(([r, u, s]) => {
        setReports(r as any[]);
        setUsers(u);
        setStrains(s);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-5">
        <Shield size={20} className="text-[var(--color-secondary)]" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Moderation</h1>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-0.5">Real reports, users, and strain status from the backend.</p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 mb-5 text-[13px] text-[var(--color-ink-muted)]">
          Admin data requires a moderator/admin session. Sign in as <span className="font-mono text-[var(--color-primary)]">malik_b</span> with <span className="font-mono text-[var(--color-primary)]">password123</span>.
        </div>
      )}

      <div className="flex gap-1 mb-5 border-b border-[var(--color-border)]">
        {(["reports", "users", "strains"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3 text-[13px] border-b-2 capitalize transition-colors",
              tab === t ? "border-[var(--color-primary)] text-[var(--color-ink)]" : "border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]",
            )}
          >
            {t}
            {t === "reports" && <Badge tone="danger">{reports.filter((r) => r.status === "open").length}</Badge>}
          </button>
        ))}
      </div>

      {tab === "reports" && (
        <div className="space-y-2">
          {reports.map((r) => (
            <div key={r.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg grid place-items-center bg-[rgba(244,63,94,0.1)] text-[var(--color-danger)] shrink-0">
                  <Flag size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge tone="muted">{r.target_type}</Badge>
                    <Badge tone={r.status === "open" ? "danger" : "amber"}>{r.status}</Badge>
                    <Badge tone="muted">{r.reason}</Badge>
                  </div>
                  <p className="text-[13px] text-[var(--color-ink-soft)] font-serif italic mb-2">{r.details}</p>
                  <div className="text-[11px] text-[var(--color-ink-faint)] font-mono">target {r.target_id}</div>
                </div>
                <Button size="xs" variant="outline" leftIcon={<Eye size={11} />} onClick={() => toast({ tone: "info", title: "Marked for review" })}>Review</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-1.5">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <Avatar initials={(u as any).avatar_initials ?? u.username.slice(0, 2).toUpperCase()} size={32} />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[var(--color-ink)]">{u.display_name ?? u.username}</div>
                <div className="text-[11px] text-[var(--color-ink-faint)] font-mono">@{u.username} · {(u as any).viewpoint}</div>
              </div>
              <Badge tone="muted">{u.role}</Badge>
              <Button size="xs" variant="ghost" className="text-[var(--color-danger)]" leftIcon={<UserX size={11} />} onClick={() => toast({ tone: "warn", title: "Suspend user?", desc: "Not implemented yet." })}>Suspend</Button>
            </div>
          ))}
        </div>
      )}

      {tab === "strains" && (
        <div className="space-y-1.5">
          {strains.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[var(--color-ink)] truncate">{s.title}</div>
                <div className="text-[11px] text-[var(--color-ink-faint)] font-mono">@{s.creator} · {s.status}</div>
              </div>
              <Badge tone="muted">{s.visibility}</Badge>
              <Button size="xs" variant="ghost" leftIcon={<EyeOff size={11} />} onClick={() => apiClient.hideStrain(s.id).then(() => toast({ tone: "ok", title: "Strain hidden" }))}>Hide</Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-[rgba(251,191,36,0.25)] bg-[rgba(251,191,36,0.05)] p-4 flex items-start gap-3">
        <AlertTriangle size={16} className="text-[var(--color-secondary)] mt-0.5 shrink-0" />
        <div className="text-[12px] text-[var(--color-ink-muted)] leading-relaxed">
          Reports and hidden statuses are persisted. Rate limits, audit logs, and fuller policy workflows are still later hardening work.
        </div>
      </div>
    </div>
  );
}
